import { forwardRef, useRef } from 'react';
import FocusWithin from 'react-focus-within';
import type { InputProps } from '..';
import * as Styled from './styled';

export * from './types';
export { Input };

const Input = forwardRef<HTMLInputElement, InputProps>((props: InputProps, ref) => {
  const {
    iconLeft,
    iconRight,
    dropdown,
    wrapperCss,
    helperText,
    textarea = false,
    theme = 'light',
    disabled = false,
    textDisabled = false,
    hasError = false,
    hideNumberArrows = false,
    mobileStyleDropdown = false,
    relativeDropdown = false,
    variant = 'regular',
    ...rest
  } = props;

  // We need to target the dropdown element to change its display
  // because it wasn't working in safari with datadog session replays
  // via css &:focus-within [data-dropdown]
  // https://tipaw.atlassian.net/browse/TC-335
  const dropdownRef = useRef<HTMLDivElement>(null);

  return (
    <FocusWithin
      onBlur={() => {
        if (dropdownRef.current) {
          dropdownRef.current.style.display = 'none';
        }
      }}
    >
      {({ focusProps }) => (
        <Styled.Wrapper
          {...focusProps}
          inputTheme={theme}
          hasDropdown={!!dropdown}
          css={wrapperCss}
          // eslint-disable-next-line jsx-a11y/tabindex-no-positive
          tabIndex={1}
          onClick={(event) => {
            if (!dropdown || !dropdownRef.current) return;

            const isSelf =
              event.currentTarget === event.target ||
              event.currentTarget === (event.target as HTMLElement).parentElement;

            if (!isSelf) return;

            const isOpen = dropdownRef.current.style.display === 'block';

            if (isOpen) {
              (document.activeElement as HTMLElement)?.blur?.();
              dropdownRef.current.style.display = 'none';
            } else {
              dropdownRef.current.style.display = 'block';
            }
          }}
        >
          {!!iconLeft && (
            <Styled.IconWrapper
              inputTheme={theme}
              textarea={textarea}
              position="left"
              hasDropdown={!!dropdown}
            >
              {iconLeft}
            </Styled.IconWrapper>
          )}
          {textarea ? (
            <Styled.Textarea
              ref={ref}
              inputTheme={theme}
              visuallyDisabled={disabled}
              disabled={disabled || textDisabled}
              {...(rest as any)}
              hasIconLeft={!!iconLeft}
              hasIconRight={!!iconRight}
              hasError={hasError}
              hideNumberArrows={hideNumberArrows}
              variant={variant}
            />
          ) : (
            <Styled.Input
              ref={ref}
              inputTheme={theme}
              visuallyDisabled={disabled}
              disabled={disabled || textDisabled}
              {...rest}
              hasIconLeft={!!iconLeft}
              hasIconRight={!!iconRight}
              hasError={hasError}
              hideNumberArrows={hideNumberArrows}
              variant={variant}
            />
          )}
          {!!iconRight && (
            <Styled.IconWrapper
              inputTheme={theme}
              textarea={textarea}
              position="right"
              hasDropdown={!!dropdown}
            >
              {iconRight}
            </Styled.IconWrapper>
          )}
          {!!helperText && <Styled.HelperText>{helperText}</Styled.HelperText>}
          {!!dropdown && (
            <Styled.DropdownWrapper
              variant={variant}
              ref={dropdownRef}
              mobileStyleDropdown={mobileStyleDropdown}
              relativeDropdown={relativeDropdown}
            >
              {mobileStyleDropdown && (
                <Styled.MobileDropdownControlWrapper>
                  <Styled.MobileDropdownCloseButton
                    onClick={() => {
                      (document.activeElement as HTMLElement)?.blur?.();
                    }}
                  >
                    Done
                  </Styled.MobileDropdownCloseButton>
                </Styled.MobileDropdownControlWrapper>
              )}
              {dropdown}
            </Styled.DropdownWrapper>
          )}
        </Styled.Wrapper>
      )}
    </FocusWithin>
  );
});

Input.displayName = 'Input';
